import { useState, useEffect, useMemo } from "react";
import { format, parseISO, startOfWeek, addDays } from "date-fns";
import { fr } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ScrollArea } from "../ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Check,
  X,
  Clock,
  AlertTriangle,
  UserCheck,
  UserX,
  ChevronLeft,
  ChevronRight,
  Calendar,
  FileText,
  Save,
  Utensils,
  Heart,
  Pill,
  ClipboardList,
} from "lucide-react";
import {
  saveDailyAttendance,
  getDailyAttendance,
  listStudents,
  getAbsenceReasons,
  justifyAbsence,
  listAbsences,
} from "../../lib/api";
import { toast } from "sonner";

const ATTENDANCE_STATUSES = [
  { value: "present", label: "Présent", icon: Check, color: "text-green-600 bg-green-50" },
  { value: "absent", label: "Absent", icon: X, color: "text-red-600 bg-red-50" },
  { value: "late", label: "Retard", icon: Clock, color: "text-orange-600 bg-orange-50" },
  { value: "excused", label: "Excusé", icon: AlertTriangle, color: "text-blue-600 bg-blue-50" },
];

function AttendanceStatusButton({ status, isSelected, onClick }) {
  const statusInfo = ATTENDANCE_STATUSES.find(s => s.value === status);
  const Icon = statusInfo?.icon || Check;
  
  return (
    <button
      onClick={onClick}
      className={`p-2 rounded-lg transition-all ${
        isSelected 
          ? `${statusInfo?.color} ring-2 ring-offset-1 ring-current` 
          : "bg-gray-100 text-gray-400 hover:bg-gray-200"
      }`}
      title={statusInfo?.label}
    >
      <Icon className="w-5 h-5" />
    </button>
  );
}

export function AttendanceModule({ onClose }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentDate, setCurrentDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [period, setPeriod] = useState("morning"); // morning or afternoon
  const [attendanceEntries, setAttendanceEntries] = useState({});
  const [absenceReasons, setAbsenceReasons] = useState([]);
  const [showJustifyDialog, setShowJustifyDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [justifyForm, setJustifyForm] = useState({ reason: "", details: "" });

  // Fetch students and attendance
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [studentsRes, reasonsRes] = await Promise.all([
          listStudents(),
          getAbsenceReasons()
        ]);
        setStudents(studentsRes.data || []);
        setAbsenceReasons(reasonsRes.data || []);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        toast.error("Erreur lors du chargement");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch attendance for current date
  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        const response = await getDailyAttendance(currentDate);
        const periodData = period === "morning" ? response.data.morning : response.data.afternoon;
        
        if (periodData?.entries) {
          const entriesMap = {};
          periodData.entries.forEach(entry => {
            entriesMap[entry.student_id] = entry;
          });
          setAttendanceEntries(entriesMap);
        } else {
          // Initialize with all students present
          const defaultEntries = {};
          students.forEach(s => {
            defaultEntries[s.id] = { student_id: s.id, status: "present" };
          });
          setAttendanceEntries(defaultEntries);
        }
      } catch (err) {
        console.error("Failed to fetch attendance:", err);
        // Initialize with all students present
        const defaultEntries = {};
        students.forEach(s => {
          defaultEntries[s.id] = { student_id: s.id, status: "present" };
        });
        setAttendanceEntries(defaultEntries);
      }
    };
    
    if (students.length > 0) {
      fetchAttendance();
    }
  }, [currentDate, period, students.length]);

  const handleStatusChange = (studentId, status) => {
    setAttendanceEntries(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        student_id: studentId,
        status,
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const entries = Object.values(attendanceEntries);
      await saveDailyAttendance({
        date: currentDate,
        period,
        entries,
      });
      toast.success("Appel enregistré");
    } catch (err) {
      console.error("Failed to save attendance:", err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleJustify = async () => {
    if (!selectedStudent || !justifyForm.reason) return;
    
    try {
      await justifyAbsence(
        currentDate,
        selectedStudent.id,
        justifyForm.reason,
        justifyForm.details
      );
      
      // Update local state
      setAttendanceEntries(prev => ({
        ...prev,
        [selectedStudent.id]: {
          ...prev[selectedStudent.id],
          justified: true,
          reason: justifyForm.reason,
          reason_details: justifyForm.details,
        }
      }));
      
      toast.success("Absence justifiée");
      setShowJustifyDialog(false);
      setSelectedStudent(null);
      setJustifyForm({ reason: "", details: "" });
    } catch (err) {
      console.error("Failed to justify absence:", err);
      toast.error("Erreur");
    }
  };

  const navigateDate = (direction) => {
    const date = parseISO(currentDate);
    const newDate = direction === "prev" 
      ? addDays(date, -1) 
      : addDays(date, 1);
    setCurrentDate(format(newDate, "yyyy-MM-dd"));
  };

  // Statistics
  const stats = useMemo(() => {
    const entries = Object.values(attendanceEntries);
    return {
      present: entries.filter(e => e.status === "present").length,
      absent: entries.filter(e => e.status === "absent").length,
      late: entries.filter(e => e.status === "late").length,
      excused: entries.filter(e => e.status === "excused").length,
      total: entries.length,
    };
  }, [attendanceEntries]);

  const formattedDate = format(parseISO(currentDate), "EEEE d MMMM yyyy", { locale: fr });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Appel</h2>
          <p className="text-sm text-slate-500 capitalize">{formattedDate}</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </div>

      {/* Date Navigation */}
      <div className="flex items-center justify-between bg-slate-50 rounded-lg p-2">
        <Button variant="ghost" size="sm" onClick={() => navigateDate("prev")}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-500" />
          <Input
            type="date"
            value={currentDate}
            onChange={(e) => setCurrentDate(e.target.value)}
            className="w-auto"
          />
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigateDate("next")}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Period Tabs */}
      <Tabs value={period} onValueChange={setPeriod}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="morning" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Matin
          </TabsTrigger>
          <TabsTrigger value="afternoon" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Après-midi
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-green-700">{stats.present}</p>
            <p className="text-xs text-green-600">Présents</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-red-700">{stats.absent}</p>
            <p className="text-xs text-red-600">Absents</p>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-orange-700">{stats.late}</p>
            <p className="text-xs text-orange-600">Retards</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-blue-700">{stats.excused}</p>
            <p className="text-xs text-blue-600">Excusés</p>
          </CardContent>
        </Card>
      </div>

      {/* Student List */}
      <ScrollArea className="h-[400px]">
        <div className="space-y-2">
          {students.map((student) => {
            const entry = attendanceEntries[student.id] || { status: "present" };
            const isAbsent = entry.status === "absent" || entry.status === "excused";
            
            return (
              <Card 
                key={student.id} 
                className={`transition-all ${
                  entry.status === "absent" && !entry.justified 
                    ? "border-red-300 bg-red-50/50" 
                    : ""
                }`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-indigo-700 font-medium">
                          {student.student_name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{student.student_name}</p>
                        <p className="text-xs text-slate-500 uppercase">{student.niveau}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {ATTENDANCE_STATUSES.map((status) => (
                        <AttendanceStatusButton
                          key={status.value}
                          status={status.value}
                          isSelected={entry.status === status.value}
                          onClick={() => handleStatusChange(student.id, status.value)}
                        />
                      ))}
                      
                      {isAbsent && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className={entry.justified ? "text-green-600" : "text-red-600"}
                          onClick={() => {
                            setSelectedStudent(student);
                            setShowJustifyDialog(true);
                          }}
                        >
                          <FileText className="w-4 h-4" />
                          <span className="ml-1 text-xs">
                            {entry.justified ? "Justifié" : "Justifier"}
                          </span>
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Show reason if justified */}
                  {entry.justified && entry.reason && (
                    <div className="mt-2 pl-13 text-xs text-slate-500">
                      <Badge variant="outline" className="text-xs">
                        {absenceReasons.find(r => r.value === entry.reason)?.label || entry.reason}
                      </Badge>
                      {entry.reason_details && (
                        <span className="ml-2">{entry.reason_details}</span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </ScrollArea>

      {/* Quick Actions */}
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          className="flex-1"
          onClick={() => {
            const newEntries = {};
            students.forEach(s => {
              newEntries[s.id] = { student_id: s.id, status: "present" };
            });
            setAttendanceEntries(newEntries);
          }}
        >
          <UserCheck className="w-4 h-4 mr-2" />
          Tous présents
        </Button>
      </div>

      {/* Justify Dialog */}
      <Dialog open={showJustifyDialog} onOpenChange={setShowJustifyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Justifier l'absence</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Élève : <strong>{selectedStudent?.student_name}</strong>
            </p>
            <div>
              <label className="text-sm font-medium">Motif</label>
              <Select
                value={justifyForm.reason}
                onValueChange={(v) => setJustifyForm(prev => ({ ...prev, reason: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionner un motif" />
                </SelectTrigger>
                <SelectContent>
                  {absenceReasons.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Détails (optionnel)</label>
              <Textarea
                value={justifyForm.details}
                onChange={(e) => setJustifyForm(prev => ({ ...prev, details: e.target.value }))}
                placeholder="Informations complémentaires..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJustifyDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleJustify} disabled={!justifyForm.reason}>
              Valider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AttendanceModule;
